import type { SessionIdentity } from "./auth";
import type { Team } from "./types";

/**
 * The squads of ONE tournament a browser's credentials make its own: the
 * teams it gets a "team workspace" shortcut to on the public board and on a
 * team's public page (2026-09-14).
 *
 * Deliberately NOT `authorize()` or `identityFor()`: those answer "may this
 * credential open the hub", and an organiser and the super may open every
 * team's. A shortcut on every team page would tell an organiser they belong
 * to all of them, so this is the narrower question - whose team is it:
 *
 * - a participant link scoped to the team;
 * - an event seat (captain or spirit captain of that squad);
 * - a seat on the club behind the squad. The club's captain, manager and
 *   spirit captain run every squad the club entered, so they get each one. A
 *   plain `member` is a player: when the club entered two or more squads here
 *   and the member's library row is on one of their rosters, only those are
 *   theirs (Вовчиці and VinWolves are one club and two different teams). A
 *   member on no roster keeps every squad, like the hub gate, rather than
 *   losing the way in.
 *
 * That narrowing is a best guess, and `members: "all"` turns it off. The
 * session carries the seat's library row but not the account's verified
 * player links, so a member who plays a sibling squad as a pickup (a row with
 * its own id) is not seen on it. The shortcuts live with that - the hub still
 * opens, and My account lists the squad - but `/t/<slug>/team`'s resolver
 * must not: it is the only chooser a club link lands on, and it offers every
 * squad the hub opens for.
 *
 * Every team returned passes the hub's own gate (`push:subscribe` at team
 * scope), so the shortcut never lands on a refusal. Roster rows are matched by
 * id only: a library import keeps the library's ids, and a name that happens
 * to match is never evidence of who someone is. A player who is on a roster
 * only through a verified link, with no seat, is not here: the hub does not
 * open for them either (`worker/player-routes.ts` sends them to the public
 * page).
 *
 * The result is in `teams` order.
 */
export function heldTeamIds(
  identities: readonly SessionIdentity[],
  tournamentId: string,
  teams: readonly Pick<Team, "id" | "clubId" | "roster">[],
  { members = "rostered" }: { members?: "rostered" | "all" } = {},
): string[] {
  const held = new Set<string>();
  const clubOf = (team: Pick<Team, "id" | "clubId">): string => team.clubId ?? team.id;
  for (const identity of identities) {
    if (identity.kind === "grant") {
      const teamId = identity.scope?.teamId;
      if (identity.role === "participant" && identity.tournamentId === tournamentId && teamId) {
        held.add(teamId);
      }
      continue;
    }
    if (identity.kind !== "account") continue;
    for (const m of identity.memberships ?? []) {
      if (m.kind === "team") {
        if (m.tournamentId !== tournamentId) continue;
        const team = teams.find((x) => x.id === m.teamId);
        if (team !== undefined && clubOf(team) === m.clubId) held.add(team.id);
      } else if (m.kind === "club") {
        const squads = teams.filter((team) => clubOf(team) === m.clubId);
        const playing =
          members === "rostered" &&
          m.role === "member" &&
          m.playerId !== undefined &&
          squads.length > 1
            ? squads.filter((team) => (team.roster ?? []).some((p) => p.id === m.playerId))
            : [];
        for (const team of playing.length > 0 ? playing : squads) held.add(team.id);
      }
    }
  }
  return teams.filter((team) => held.has(team.id)).map((team) => team.id);
}
